import React, { useState } from "react";
import { ChipInput } from "../brand/components/ChipInput";
import { validateWebsiteUrl } from "../brand/utils/brandFormValidation";

/**
 * Form-first onboarding (SGL-448): the few fields that create a Brand, with no
 * AI and no website required. Everything else is learned later or added in
 * Brand details.
 */

export interface QuickStartValues {
  businessName: string;
  city: string;
  state: string;
  /** Postal code; required format only in the US (customer-management checks it). */
  zip: string;
  country: string;
  website: string;
  services: string[];
}

interface Props {
  initial?: Partial<QuickStartValues>;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: QuickStartValues) => void;
  /** Offer the assistant path ("let our assistant read your website"). */
  onUseAssistant?: () => void;
}

const inputClass =
  "w-full rounded-xl border border-theme-border bg-theme-surface px-3 py-2.5 text-[15px] text-theme-fg outline-none transition placeholder:text-theme-fg-muted focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";
const labelClass = "mb-1 block text-[13px] font-semibold text-theme-fg";
const optional = <span className="font-normal text-theme-fg-muted">(optional)</span>;

export const QuickStartForm: React.FC<Props> = ({ initial, submitting, error, onSubmit, onUseAssistant }) => {
  const [values, setValues] = useState<QuickStartValues>({
    businessName: initial?.businessName ?? "",
    city: initial?.city ?? "",
    state: initial?.state ?? "",
    zip: initial?.zip ?? "",
    // Shown pre-selected so the owner sees it and can change it; most of our
    // customers are in the US.
    country: initial?.country || "United States",
    website: initial?.website ?? "",
    services: initial?.services ?? [],
  });
  const set = <K extends keyof QuickStartValues>(k: K, v: QuickStartValues[K]) =>
    setValues((prev) => ({ ...prev, [k]: v }));

  const site = validateWebsiteUrl(values.website);
  const missing: string[] = [];
  if (!values.businessName.trim()) missing.push("business name");
  if (!values.city.trim()) missing.push("city");
  if (!values.state.trim()) missing.push("state");
  if (!values.country.trim()) missing.push("country");
  const canSubmit = missing.length === 0 && site.isValid && !submitting;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) onSubmit(values);
  };

  return (
    <form
      onSubmit={submit}
      aria-label="Tell us about your business"
      className="w-full max-w-[560px] rounded-2xl border border-theme-border bg-theme-surface p-5 text-left shadow-sm sm:p-6"
    >
      <h2 className="text-[18px] font-semibold text-theme-fg">Tell us about your business</h2>
      <p className="mt-1 text-[13px] text-theme-fg-muted">
        Takes a minute. Your AI employees are created as soon as you finish.
      </p>

      <div className="mt-5 flex flex-col gap-4">
        <div>
          <label htmlFor="qs-name" className={labelClass}>Business name</label>
          <input id="qs-name" className={inputClass} value={values.businessName}
            onChange={(e) => set("businessName", e.target.value)} placeholder="e.g. San Gabriel Pressure Wash" autoComplete="organization" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label htmlFor="qs-city" className={labelClass}>City</label>
            <input id="qs-city" className={inputClass} value={values.city}
              onChange={(e) => set("city", e.target.value)} placeholder="Georgetown" autoComplete="address-level2" />
          </div>
          <div>
            <label htmlFor="qs-state" className={labelClass}>State</label>
            <input id="qs-state" className={inputClass} value={values.state}
              onChange={(e) => set("state", e.target.value)} placeholder="TX" autoComplete="address-level1" />
          </div>
          <div>
            <label htmlFor="qs-zip" className={labelClass}>Zip code {optional}</label>
            <input id="qs-zip" className={inputClass} value={values.zip}
              onChange={(e) => set("zip", e.target.value)} placeholder="78626" autoComplete="postal-code" />
          </div>
          <div>
            <label htmlFor="qs-country" className={labelClass}>Country</label>
            <input id="qs-country" className={inputClass} value={values.country}
              onChange={(e) => set("country", e.target.value)} autoComplete="country-name" />
          </div>
        </div>
        <ChipInput
          label="What do you do? (optional)"
          chips={values.services}
          onChipsChange={(next: string[]) => set("services", next)}
          placeholder="e.g. Pressure washing (press Enter after each)"
          helperText="Recommended: your agents write much better posts when they know this."
        />
        <div>
          <label htmlFor="qs-site" className={labelClass}>Website {optional}</label>
          <input id="qs-site" className={inputClass} value={values.website}
            onChange={(e) => set("website", e.target.value)} placeholder="yourbusiness.com" inputMode="url" autoComplete="url" />
          {!site.isValid && <p className="mt-1 text-[12px] text-red-600" role="alert">{site.error}</p>}
          <p className="mt-1 text-[12px] text-theme-fg-muted">If you have one, we'll read it in the background to learn more about you.</p>
        </div>
      </div>

      {error && <p className="mt-4 text-[13px] text-red-600" role="alert">{error}</p>}
      {missing.length > 0 && !error && (
        <p className="mt-4 text-[13px] text-theme-fg-muted" role="status">Add your {missing.join(", ")} to continue.</p>
      )}

      <button type="submit" disabled={!canSubmit}
        className="mt-5 h-11 w-full rounded-xl bg-brand-primary px-5 text-[15px] font-semibold text-white shadow-sm transition hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-50">
        {submitting ? "Setting up your AI team…" : "Create my profile →"}
      </button>

      {onUseAssistant && (
        <button type="button" onClick={onUseAssistant}
          className="mt-3 w-full text-center text-[13px] font-medium text-theme-fg-muted underline-offset-2 hover:text-brand-primary hover:underline">
          Or let our assistant read your website instead
        </button>
      )}
    </form>
  );
};

export default QuickStartForm;
